import Seo from "@/components/Seo";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, Copy, Download, CheckCircle2 } from "lucide-react";

import { PassAlgarveLogo } from "@/components/PassAlgarveLogo";

export default function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setup2FA, verify2FASetup } = useAuth();

  const tempToken = location.state?.temp_token;
  const email = location.state?.email;

  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [step, setStep] = useState("scan"); // scan | backup | done
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    if (!tempToken) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const data = await setup2FA(tempToken);
        setQrDataUrl(data.qr_code_data_url);
        setSecret(data.secret);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro ao iniciar 2FA");
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code) return;
    setVerifying(true);
    try {
      const { backup_codes } = await verify2FASetup(tempToken, code);
      setBackupCodes(backup_codes || []);
      setStep("backup");
      toast.success("2FA ativado com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código inválido");
    } finally {
      setVerifying(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Códigos copiados para a área de transferência");
  };

  const downloadBackupCodes = () => {
    const text =
      "PASS ALGARVE — Códigos de Backup 2FA\n" +
      `Conta: ${email}\n` +
      `Data: ${new Date().toLocaleString("pt-PT")}\n\n` +
      "Guarde estes códigos num local seguro. Cada código só pode ser usado UMA vez.\n\n" +
      backupCodes.map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`).join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pass-algarve-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-navy-dark flex flex-col">
      <Seo noindex title="Configurar 2FA" />
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          className="w-full max-w-lg relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
        <Card className="bg-navy border-gold/20 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <PassAlgarveLogo size="default" />
            </div>
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center">
                <Shield className="w-7 h-7 text-gold" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl text-white">
              {step === "scan" && "Configurar 2FA"}
              {step === "backup" && "Códigos de Backup"}
            </CardTitle>
            <CardDescription className="text-white/60 font-body">
              {step === "scan" && "Proteja a sua conta de administrador com autenticação de dois fatores"}
              {step === "backup" && "Guarde estes códigos agora — não serão mostrados novamente"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gold"></div>
              </div>
            ) : step === "scan" ? (
              <div className="space-y-5">
                <div className="bg-white rounded-xl p-4 flex justify-center">
                  <img src={qrDataUrl} alt="QR Code 2FA" className="w-52 h-52" data-testid="twofa-qr-img" />
                </div>

                <div className="bg-navy-dark/60 rounded-lg p-3 border border-gold/10">
                  <p className="text-white/60 font-body text-xs mb-1">Ou insira manualmente:</p>
                  <p className="text-white font-mono text-sm break-all" data-testid="twofa-secret">{secret}</p>
                </div>

                <ol className="text-white/70 font-body text-sm space-y-2 list-decimal list-inside">
                  <li>Abra o <strong>Google Authenticator</strong>, Authy ou 1Password</li>
                  <li>Toque em <strong>+</strong> → Ler código QR</li>
                  <li>Scaneie o código acima</li>
                  <li>Insira o código de 6 dígitos gerado</li>
                </ol>

                <form onSubmit={handleVerify} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="code" className="text-white/80 font-body">Código de 6 dígitos</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      required
                      className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20 text-center text-2xl tracking-widest"
                      data-testid="twofa-setup-code-input"
                      autoFocus
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={verifying || code.length !== 6}
                    className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full"
                    data-testid="twofa-setup-verify-btn"
                  >
                    {verifying ? "Verificando..." : "Ativar 2FA"}
                  </Button>
                </form>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-200 font-body text-sm font-semibold">Guarde estes códigos com segurança!</p>
                    <p className="text-amber-200/70 font-body text-xs mt-1">
                      Use-os se perder o acesso ao autenticador. Cada código só funciona uma vez.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2" data-testid="twofa-backup-codes">
                  {backupCodes.map((c, i) => (
                    <div
                      key={i}
                      className="bg-navy-dark/60 border border-gold/10 rounded-lg py-2 px-3 font-mono text-white text-center text-sm"
                    >
                      {c}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={copyBackupCodes}
                    className="border-gold/30 text-white hover:bg-white/5 rounded-full"
                    data-testid="twofa-copy-codes-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadBackupCodes}
                    className="border-gold/30 text-white hover:bg-white/5 rounded-full"
                    data-testid="twofa-download-codes-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>

                <Button
                  onClick={() => navigate("/admin")}
                  className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full mt-2"
                  data-testid="twofa-finish-btn"
                >
                  Continuar para o Admin
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
      </div>
    </div>
  );
}
